import type { ChangeEvent, KeyboardEvent } from "react"
import { useEffect, useRef, useState } from "react"
import Picker from "@emoji-mart/react"
import { t } from "@lingui/core/macro"
import { ShortcodeParser } from "@multimediallc/web-utils/shortcodeParsing"
import { isEdge, isIOS, isMobile } from "react-device-detect"
import { useParams } from "react-router-dom"
import { usePerformToggle } from "../../../hooks/appContext"
import { useOutsideClick } from "../../../hooks/useOutsideClick"
import { useThrottle } from "../../../hooks/useThrottle"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import {
    addMessageToHistory,
    useGetProfileQuery,
    usePublishMessageMutation,
    useTypingIndicatorMutation,
} from "../../../store/messagingSlice"
import { debug } from "../../../utils/debug"
import { addPageAction } from "../../../utils/newrelic"
import { Textarea } from "../../common"
import { Camera, Gallery, Smile } from "../../common/atoms/Icons/Chat"
import { IconButton } from "../common/IconButton/IconButton"
import { draftCache } from "../DraftCache/DraftCache"
import {
    useBlurOnViewportScroll,
    useIOSInputFocusOutFix,
    useMessagingView,
} from "../hooks"
import { createLogMessage } from "../MessagingContext"
import { SendTip } from "../SendTip/SendTip"
import { classNames } from "../utils"
import styles from "./MessageInputBar.module.scss"
import { MessageMediaSelect } from "./MessageMediaSelect/MessageMediaSelect"
import { SubmitFormIcon } from "./SubmitFormIcon/SubmitFormIcon"

const TIP_COMMAND = "/tip"

type Props = {
    isDataLoading: boolean
    shouldHide: boolean
    scrollToBottom: () => void
    onViewportHeightChange: (delta?: number) => void
    onMessageInputFocusChange: (hasFocus: boolean) => void
    trackNewConversationMessageSent: () => void
    onSendMessage: () => void
}

const MIN_TYPING_INDICATOR_REQUEST_PERIOD_MS = 3000
const PUBLISH_OK_STATUS = "Ok"
const ANDROID_KEYBOARD_TOGGLE_DELAY_MS = 100
const IOS_KEYBOARD_TOGGLE_DELAY_MS = 250
const KEYBOARD_TOGGLE_DELAY = isIOS
    ? IOS_KEYBOARD_TOGGLE_DELAY_MS
    : ANDROID_KEYBOARD_TOGGLE_DELAY_MS

export function MessageInputBar({
    isDataLoading,
    shouldHide,
    scrollToBottom,
    onViewportHeightChange,
    onMessageInputFocusChange,
    trackNewConversationMessageSent,
    onSendMessage,
}: Props) {
    const messagingView = useMessagingView()
    const { username } = useParams<{ username: string }>()
    const me = useAppSelector((state) => state.user.loggedInUser)
    const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery(
        username || "",
    )
    const [publishMessage] = usePublishMessageMutation()
    const [text, setText] = useState("")
    const [typingIndicator] = useTypingIndicatorMutation()
    const throttledText = useThrottle(
        text,
        MIN_TYPING_INDICATOR_REQUEST_PERIOD_MS,
        (val) => val.trim() === "",
    )

    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [showSendTip, setShowSendTip] = useState(false)
    const [emojiData, setEmojiData] = useState<any>(null)
    const showSend = text.trim().length > 0
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const selectedMediaInputRef = useRef<HTMLInputElement>(null)
    const smileIconRef = useRef<HTMLDivElement>(null)
    const [selectedMedia, setSelectedMedia] = useState<File[]>([])
    const [initialTipAmount, setInitialTipAmount] = useState<string>("")
    const isDmMediaActive = usePerformToggle("DmMedia").active
    const isDesktopDmActive = usePerformToggle("DesktopDm").active
    const isDarkMode = document.body.classList.contains("darkmode")

    useBlurOnViewportScroll(textareaRef)

    const handleTipCommand = (trimmedText: string) => {
        setText("")
        if (profile?.can_tip) {
            const tipAmount = trimmedText.toLowerCase().split(" ")[1]
            const numericTipAmount = Number(tipAmount)
            if (!isNaN(numericTipAmount) && numericTipAmount > 0) {
                setInitialTipAmount(numericTipAmount.toString())
            }
            setShowSendTip(true)
        }
    }

    const emojiPickerRef = useOutsideClick(() => {
        setShowEmojiPicker(false)
    }, [smileIconRef])
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (showEmojiPicker && !emojiData) {
            import("@emoji-mart/data").then((module) =>
                setEmojiData(module.default),
            )
        }
    }, [showEmojiPicker, emojiData])

    useEffect(() => {
        updateTextAreaSize()
    }, [text])

    const handleSubmit = () => {
        const trimmedText = text.trim()

        if (trimmedText.toLowerCase().startsWith(TIP_COMMAND)) {
            handleTipCommand(trimmedText)
            return
        }

        setText("")
        setSelectedMedia([])
        setShowEmojiPicker(false)
        draftCache.clear(username || "")

        try {
            if (!username || !me) throw new Error("No from_user or to_user")

            if (ShortcodeParser.isShortcodeSyntax(trimmedText)) {
                dispatch(
                    addMessageToHistory(
                        username,
                        createLogMessage(
                            username,
                            t`Shortcodes are not supported in Messages.`,
                        ),
                    ),
                )
                return
            }

            onSendMessage()
            publishMessage({
                message: trimmedText,
                to_user: username,
                from_user: me?.username,
            })
                .unwrap()
                .then((result) => {
                    if (
                        (result.status !== PUBLISH_OK_STATUS ||
                            !result?.status) &&
                        username
                    ) {
                        dispatch(
                            addMessageToHistory(
                                username,
                                createLogMessage(
                                    username,
                                    result.status || t`Unable to send message`,
                                ),
                            ),
                        )
                        draftCache.createGhostDraft(username)
                    }

                    addPageAction("SendPrivateMessage", {
                        source: "DM",
                        is_reply: result?.is_reply ?? false,
                        token_color: result?.token_color ?? undefined,
                        in_fanclub: result?.in_fanclub ?? false,
                        success: result.status === PUBLISH_OK_STATUS,
                    })

                    trackNewConversationMessageSent()
                })
                .catch((result) => {
                    let errorMessage = t`Unable to send message`
                    if (result.data?.error) {
                        errorMessage = result.data.error
                    }
                    dispatch(
                        addMessageToHistory(
                            username,
                            createLogMessage(username, errorMessage),
                        ),
                    )
                })

            if (textareaRef.current) {
                textareaRef.current.focus()
            }
        } catch (e) {
            debug(e)
        }
    }

    useEffect(() => {
        if (text.trim() && username && me && profile?.can_pm) {
            typingIndicator({ to_username: username })
        }
    }, [throttledText, username, me, profile?.can_pm, typingIndicator])

    const handleEmojiSelect = (emoji: any) => {
        setText((prev) => prev + emoji.native)
    }

    const updateTextAreaSize = () => {
        const textarea = textareaRef.current
        if (!textarea) return

        const lineHeight = 20
        const maxLines = 5
        const maxHeight = maxLines * lineHeight

        const previousHeight = textarea.style.height
            ? parseInt(textarea.style.height)
            : 0

        textarea.style.height = ""
        const newHeight = Math.min(textarea.scrollHeight, maxHeight)
        textarea.style.height = `${newHeight}px`

        if (newHeight !== previousHeight) {
            onViewportHeightChange(newHeight - previousHeight)
        }
    }

    const handleTokensClick = () => {
        if (isProfileLoading || isDataLoading) return

        addPageAction("DmTipMenu", {
            actionType: "open",
            canTip: profile?.can_tip,
        })

        if (profile?.can_tip) {
            setShowSendTip(true)
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isMobile && e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            if (text.trim()) {
                handleSubmit()
            }
        }
    }

    const handleResize = () => {
        setTimeout(() => {
            // do not update scroll position if the message input isn't focused, unless you're on Edge
            if (textareaRef.current !== document.activeElement && !isEdge) {
                return
            }

            requestAnimationFrame(() => {
                onViewportHeightChange()
            })
        }, KEYBOARD_TOGGLE_DELAY)
    }

    const handleTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value)
        if (!username) return
        const draft = draftCache.get(username) || {}
        draftCache.set(username, {
            ...draft,
            timestamp: Date.now(),
            message: e.target.value,
        })
    }

    useEffect(() => {
        window.visualViewport?.addEventListener("resize", handleResize)
        return () => {
            window.visualViewport?.removeEventListener("resize", handleResize)
        }
    }, [handleResize])

    useEffect(() => {
        const cachedDraft = draftCache.get(username || "")
        if (cachedDraft?.message) {
            setText(cachedDraft.message)
        }
    }, [username])

    useIOSInputFocusOutFix(textareaRef)
    const isDesktop = !isMobile

    return (
        <>
            <div
                className={classNames(styles.container, {
                    [styles.hide]: shouldHide,
                    [styles.desktop]: isDesktop,
                })}
            >
                <MessageMediaSelect
                    selectedMedia={selectedMedia}
                    setSelectedMedia={setSelectedMedia}
                    selectedMediaInputRef={selectedMediaInputRef}
                    scrollToBottom={scrollToBottom}
                />
                <div
                    className={classNames(styles.input, {
                        [styles.mobile]: isMobile,
                        [styles.desktop]: messagingView.isDesktopConversation,
                    })}
                >
                    {isDmMediaActive && (
                        <>
                            <IconButton
                                data-testid="camera-btn"
                                className={styles.iconButton}
                                icon={<Camera height={20} width={20} />}
                                iconAlt={t`Take photo`}
                                onClick={() => {
                                    selectedMediaInputRef.current?.click()
                                }}
                            />
                            <IconButton
                                data-testid="gallery-btn"
                                className={styles.iconButton}
                                icon={<Gallery height={20} width={20} />}
                                iconAlt={t`Choose from gallery`}
                            />
                        </>
                    )}
                    {!showSendTip && (
                        <>
                            <div className={styles.inputWrapper}>
                                <div className={styles.textareaContainer}>
                                    <Textarea
                                        className={styles.textarea}
                                        placeholder={t`Send a message...`}
                                        value={text}
                                        onChange={handleTextAreaChange}
                                        onKeyDown={handleKeyDown}
                                        rows={1}
                                        ref={textareaRef}
                                        onFocus={() =>
                                            onMessageInputFocusChange(true)
                                        }
                                        onBlur={() =>
                                            onMessageInputFocusChange(false)
                                        }
                                        data-testid="input-message-bar-text"
                                    />
                                </div>
                                {isDesktopDmActive &&
                                    !messagingView.isMobile && (
                                        <div
                                            className={styles.smileIcon}
                                            ref={smileIconRef}
                                        >
                                            <Smile
                                                height={20}
                                                width={20}
                                                onClick={() => {
                                                    setShowEmojiPicker(
                                                        !showEmojiPicker,
                                                    )
                                                    addPageAction("DmEmojiMenu")
                                                }}
                                                className={classNames({
                                                    [styles.active]:
                                                        showEmojiPicker,
                                                })}
                                            />
                                        </div>
                                    )}
                            </div>
                            {showEmojiPicker &&
                                emojiData &&
                                isDesktopDmActive &&
                                !messagingView.isMobile && (
                                    <div
                                        className={styles.emojiPicker}
                                        ref={emojiPickerRef}
                                    >
                                        <Picker
                                            data={emojiData}
                                            onEmojiSelect={handleEmojiSelect}
                                            previewPosition="none"
                                            perLine={9}
                                            emojiSize={20}
                                            emojiButtonSize={27}
                                            skinTonePosition="none"
                                            theme={
                                                isDarkMode ? "dark" : "light"
                                            }
                                        />
                                        <svg
                                            className={
                                                styles.emojiPickerTriangle
                                            }
                                            width="20"
                                            height="10"
                                            viewBox="0 0 20 10"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M0,0 H20 L12,8 Q10,10 8,8 Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                    </div>
                                )}
                            <SubmitFormIcon
                                isMobile={isMobile}
                                isLoading={isProfileLoading || isDataLoading}
                                canSend={showSend}
                                canTip={profile?.can_tip}
                                handleSendMessage={handleSubmit}
                                handleSendTokens={handleTokensClick}
                            />
                        </>
                    )}
                </div>
            </div>
            {showSendTip && username && (
                <SendTip
                    username={username}
                    onClose={() => {
                        setShowSendTip(false)
                        setInitialTipAmount("")
                    }}
                    initialTipAmount={initialTipAmount}
                    trackNewConversationMessageSent={
                        trackNewConversationMessageSent
                    }
                />
            )}
        </>
    )
}
